import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./pages/Home";
// Marketing routes other than Home are split into their own chunks so
// the home critical path only carries what the landing page renders.
// Layout wraps its <Outlet /> in Suspense, so navigation keeps the
// header + footer on screen while a route chunk loads.
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Work = lazy(() => import("./pages/Work"));
const CaseDetail = lazy(() => import("./pages/CaseDetail"));
const About = lazy(() => import("./pages/About"));
const Process = lazy(() => import("./pages/Process"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Labs = lazy(() => import("./pages/Labs"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Subscribed = lazy(() => import("./pages/Subscribed"));
const Unsubscribed = lazy(() => import("./pages/Unsubscribed"));
import NotFound from "./pages/NotFound";

/**
 * Portal surface is lazy-loaded (Phase 14 Loop 1).
 *
 * Before this change, every marketing visitor downloaded the full
 * Radix Dialog + Popover + CommandPalette tree because PortalLayout
 * was eagerly imported. The new UX primitives (GlobalSearch,
 * NotificationsBell, MobileNav, ThemeToggle, ActivityFeed) would
 * have pushed the home critical path past the 140 KB gz budget.
 * Lazy-loading the portal entry chunk drops the portal deps out of
 * the home entry graph entirely; they stream in after the login
 * form mounts or when the user navigates into /portal/*.
 */
const PortalIndex = lazy(() => import("./pages/portal/PortalIndex"));
const PortalLogin = lazy(() => import("./pages/portal/Login"));
const PortalForgot = lazy(() => import("./pages/portal/Forgot"));
const PortalFirstLogin = lazy(() => import("./pages/portal/FirstLogin"));
const PortalWelcome = lazy(() => import("./pages/portal/Welcome"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));
const AdminOverview = lazy(() => import("./pages/portal/AdminOverview"));
const AdminClients = lazy(() => import("./pages/portal/AdminClients"));
const AdminClientsNew = lazy(() => import("./pages/portal/AdminClientsNew"));
const AdminLeads = lazy(() => import("./pages/portal/AdminLeads"));
const AdminActivity = lazy(() => import("./pages/portal/AdminActivity"));
const AdminAssistantLogs = lazy(() => import("./pages/portal/AdminAssistantLogs"));
const AdminInbox = lazy(() => import("./pages/portal/AdminInbox"));
const AdminNewsletter = lazy(() => import("./pages/portal/AdminNewsletter"));
const AdminInvoices = lazy(() => import("./pages/portal/AdminInvoices"));
const AdminInvoiceDetail = lazy(() => import("./pages/portal/AdminInvoiceDetail"));
const AdminCatalog = lazy(() => import("./pages/portal/AdminCatalog"));
const AdminMailboxes = lazy(() => import("./pages/portal/AdminMailboxes"));
const AdminClientDetail = lazy(() => import("./pages/portal/AdminClientDetail"));
const ClientOverview = lazy(() => import("./pages/portal/ClientOverview"));
const ClientInbox = lazy(() => import("./pages/portal/ClientInbox"));
const ClientSettings = lazy(() => import("./pages/portal/ClientSettings"));
const PortalSettings = lazy(() => import("./pages/portal/Settings"));
const PortalPlaceholder = lazy(() => import("./pages/portal/Placeholder"));
// Rev 35 — Agentic Request Pipeline
const ClientRequests = lazy(() => import("./pages/portal/Requests"));
const AdminRequestsQueue = lazy(() => import("./pages/portal/admin/RequestsQueue"));

function PortalFallback() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-nx-bg text-nx-text-dim">
      <div className="text-sm">Loading portal…</div>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<PortalFallback />}>
      <Routes>
        {/* Marketing */}
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<CaseDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/process" element={<Process />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/labs" element={<Labs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/subscribed" element={<Subscribed />} />
          <Route path="/unsubscribed" element={<Unsubscribed />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Auth screens (no chrome) */}
        {/* /portal is the smart-redirect index. Authenticated → role-
            correct destination; unauthenticated → /portal/login.
            Marketing-site CTAs link here instead of hardcoding
            /portal/login (Bug 4 fix). */}
        <Route path="/portal" element={<PortalIndex />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal/forgot" element={<PortalForgot />} />
        <Route path="/portal/first-login" element={<PortalFirstLogin />} />
        {/* Stripe Checkout success_url lands here after a successful
            Care subscription. Public-facing post-payment landing — no
            auth required; the welcome email carries the temp password.
            Distinct from /portal (smart-redirect) so PortalIndex's
            getMe() check doesn't bounce a paying customer who hasn't
            signed in yet. */}
        <Route path="/portal/welcome" element={<PortalWelcome />} />

        {/* Admin portal */}
        <Route path="/portal/admin" element={<PortalLayout requireRole="admin" />}>
          <Route index element={<AdminOverview />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="clients/new" element={<AdminClientsNew />} />
          <Route path="clients/:id" element={<AdminClientDetail />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="assistant-logs" element={<AdminAssistantLogs />} />
          <Route path="inbox" element={<AdminInbox />} />
          {/* Rev 34 WS-2 — mailbox management */}
          <Route path="mailboxes" element={<AdminMailboxes />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="invoices" element={<AdminInvoices />} />
          <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
          <Route path="catalog" element={<AdminCatalog />} />
          {/* Rev 35 — Agentic Request Pipeline: engineer/owner queue */}
          <Route path="requests" element={<AdminRequestsQueue />} />
          <Route
            path="campaigns"
            element={<PortalPlaceholder title="Campaigns" description="Compose, test, send via SES." path="/portal/admin/campaigns" />}
          />
          {/* Rev 25.6 Loop C — /portal/admin/audit was a placeholder
              that never fetched. Both /audit and /activity now render
              the same AdminActivity component (which calls
              /v1/admin/activity); /audit is the canonical immutable-
              log URL kept for sidebar nav + sitemap, /activity is
              the same view kept for backwards-compatible links. */}
          <Route path="audit" element={<AdminActivity />} />
          <Route path="activity" element={<AdminActivity />} />
          <Route path="settings" element={<PortalSettings />} />
        </Route>

        {/* Client portal */}
        <Route path="/portal" element={<PortalLayout requireRole="client" />}>
          <Route path="overview" element={<ClientOverview />} />
          {/* Rev 32 — client inbox; mirrors admin inbox, client-scoped via /v1/portal/threads* */}
          <Route path="inbox" element={<ClientInbox />} />
          {/* Rev 35 — Agentic Request Pipeline: client submit/track experience */}
          <Route path="requests" element={<ClientRequests />} />
          {/* Rev 33 — client settings; includes email signatures */}
          <Route path="settings" element={<ClientSettings />} />
          <Route
            path="messages"
            element={<PortalPlaceholder title="Messages" description="Your threads with NEXIRT." path="/portal/messages" />}
          />
          <Route
            path="files"
            element={<PortalPlaceholder title="Files" description="Shared document vault." path="/portal/files" />}
          />
          <Route
            path="account"
            element={<PortalPlaceholder title="Account" description="Profile, security, email prefs." path="/portal/account" />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
}
